import { useCallback, useId, useMemo, useRef } from "react";
import { mergeProps, useFocusRing, useHover, useListBoxSection, usePress } from "react-aria";

import { baseCheckbox } from "../checkbox/Checkbox";
import { CheckboxIcon } from "../checkbox/CheckboxIcon";
import { listboxItemVariants } from "./ListBoxItem";
import { listBoxSectionVariants } from "./ListBoxSection";

import type { Key, ListState } from "react-stately";

interface Props<T extends object> {
  state: ListState<T>;
  label?: string;
  sectionTitle?: string;
  inputValue?: string;
  filteredKeys?: Set<Key>;
  onSelectFiltered?: (keys: Set<Key>) => void;
}

export const ListBoxSelectAll = <T extends object>({
  state,
  label = "All Items",
  sectionTitle = "Select All",
  inputValue = "",
  filteredKeys,
  onSelectFiltered,
}: Props<T>) => {
  const hasSearchTerm = inputValue.trim().length > 0;
  const isSelectAll = state.selectionManager.isSelectAll;
  const areAllFilteredSelected = useMemo(() => {
    if (!hasSearchTerm || !filteredKeys || filteredKeys.size === 0) return false;
    return Array.from(filteredKeys).every(key => state.selectionManager.isSelected(key));
  }, [hasSearchTerm, filteredKeys, state.selectionManager]);

  const isSelected = hasSearchTerm ? areAllFilteredSelected : isSelectAll;
  const isDisabled = state.selectionManager.selectionMode !== "multiple";
  const isSelectable = state.selectionManager.selectionMode !== "none";

  const displayLabel = hasSearchTerm ? "Select Filtered" : label;
  const displaySectionTitle = hasSearchTerm ? "Select Filtered" : sectionTitle;

  const domRef = useRef<HTMLLIElement>(null);

  const sectionSlots = useMemo(() => listBoxSectionVariants(), []);
  const itemSlots = useMemo(() => listboxItemVariants(), []);

  const checkboxSlots = useMemo(() => baseCheckbox({ size: "sm" }), []);

  const {
    itemProps: sectionProps,
    headingProps,
    groupProps,
  } = useListBoxSection({
    heading: displaySectionTitle,
    "aria-label": displayLabel,
  });

  const { pressProps, isPressed } = usePress({
    ref: domRef,
    isDisabled,
  });

  const { isHovered, hoverProps } = useHover({
    isDisabled,
  });

  const { isFocusVisible, focusProps } = useFocusRing();

  const labelId = useId();

  const onClick = useCallback(() => {
    if (hasSearchTerm && filteredKeys && onSelectFiltered) {
      if (areAllFilteredSelected) {
        // Deselect filtered items
        const currentSelection = new Set(state.selectionManager.selectedKeys);
        filteredKeys.forEach(key => currentSelection.delete(key));
        state.selectionManager.setSelectedKeys(currentSelection);
      } else {
        // Select filtered items
        onSelectFiltered(filteredKeys);
      }
    } else {
      state.selectionManager.toggleSelectAll();
    }
  }, [
    hasSearchTerm,
    filteredKeys,
    onSelectFiltered,
    areAllFilteredSelected,
    state.selectionManager,
  ]);

  const itemProps = useMemo(
    () => ({
      ref: domRef,
      ...mergeProps({ onClick }, focusProps, pressProps, hoverProps),
      "aria-disabled": isDisabled,
      "aria-selected": isSelected,
      "data-selectable": isSelectable,
      "data-hover": isHovered,
      "data-disabled": isDisabled,
      "data-selected": isSelected,
      "data-pressed": isPressed,
      "data-focus-visible": isFocusVisible,
      "data-slot": "wrapper",
      role: "option",
      className: itemSlots.wrapper(),
    }),
    [
      domRef,
      onClick,
      focusProps,
      pressProps,
      hoverProps,
      isSelectable,
      isDisabled,
      isSelected,
      isPressed,
      isHovered,
      isFocusVisible,
      itemSlots,
    ]
  );

  const titleProps = useMemo(
    () => ({
      id: labelId,
      "data-label": true,
      "data-slot": "title",
      className: itemSlots.title(),
    }),
    [labelId, itemSlots]
  );

  const checkboxIconProps = useMemo(
    () => ({
      "aria-hidden": true,
      "data-disabled": isDisabled,
      className: checkboxSlots.iconWrapper({ className: "mr-1" }),
    }),
    [isDisabled, checkboxSlots]
  );

  const checkboxIconInnerProps = useMemo(
    () => ({
      className: checkboxSlots.icon(),
      isSelected,
      isIndeterminate: false,
      disableAnimation: false,
      "data-checked": String(isSelected),
    }),
    [isSelected, checkboxSlots]
  );

  return (
    <li key="select-all" data-slot="base" {...sectionProps} className={sectionSlots.base()}>
      <span {...headingProps} className={sectionSlots.heading()} data-slot="heading">
        {displaySectionTitle}
      </span>
      <ul {...groupProps} className={sectionSlots.group()} data-has-title data-slot="group">
        <li {...itemProps}>
          <span {...checkboxIconProps}>
            <CheckboxIcon {...checkboxIconInnerProps} />
          </span>
          <span {...titleProps}>{displayLabel}</span>
        </li>
      </ul>
    </li>
  );
};
